import React, {useEffect, useState} from "react";
import {useTranslation} from 'react-i18next';
import {useToast} from '../../modules/layout/components/ToastContext';
import {deleteById, getAll, getById, update} from "../_request";
import {
    IAxiosGetResponse,
    IFilterTransactionRouteModel,
    IServiceDto,
    ITransactionRouteDto
} from "../../interfaces/IUtilities";
import {Link, useNavigate, useParams} from "react-router-dom";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import { FileDiff, FileEarmarkText, FileLock2, ListUl, Pencil, Trash3, JournalBookmark, XCircle, CheckCircle, Archive, Trash3Fill } from "react-bootstrap-icons";
import {Field, Form, Formik} from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import SortDirection from "../../modules/layout/components/SortDirection";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import {AxiosResponse} from "axios";
import {userHasRole} from "../../../setup/auth/authService";
import Pagination from "../../modules/layout/components/Pagination";
import { AdminWeb, Transactions_Approve, Transactions_HardDelete } from "../../constants/constants";


const TransationList = () => {
    const {t} = useTranslation();
    const {serviceid} = useParams<{ serviceid: string }>();
    const {showToast} = useToast();
    const [transactions, setTransactions] = useState([] as ITransactionRouteDto[]);
    const [service, setService] = useState({} as IServiceDto);
    const [total, setTotal] = useState(0);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("routeLabel");
    const [asc, setAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    let order = asc ? "asc" : "desc";
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [canApproveTransactions, setCanApproveTransactions] = useState(false);
    const [canHardDeleteTransactions, setCanHardDeleteTransactions] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);

    const initialFormState = {
        routeLabel: '',
        className: '',
        postLocation: '',
        showDeleted: false
    }
    const [activeFilters, setActiveFilters] = useState<IFilterTransactionRouteModel>(initialFormState);

    const fetchServiceDetails = async () => {
        try {
            let result = await getById("/api/services/", serviceid!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setService(result.data as IServiceDto);
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }

    const fetchTransactionRoutes = async (filters: IFilterTransactionRouteModel = {}) => {
        try {
            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];

            if (service.isDeleted) {
                filters.showDeleted = true;
                setActiveFilters(filters);
            }

            //if (filters.id) params.push({ key: 'id', value: filters.id });
             if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.routeLabel) params.push({ key: 'routeLabel', value: filters.routeLabel });
            if (filters.className) params.push({ key: 'className', value: filters.className });
            if (filters.postLocation) params.push({ key: 'postLocation', value: filters.postLocation });
            if (filters.showDeleted) params.push({ key: 'showDeleted', value: filters.showDeleted === true ? 1 : 0 });

            let result = await getAll(`/api/transactionroutes/getall/${serviceid}`,
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data.data as any;
            dataResults = dataResults["items"] as ITransactionRouteDto[];
            setTotal(result?.data.data.totalCount);

            let dataWarnings = result?.data.warnings as any;
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransactions(dataResults);
                setLoading(false);

                if (dataWarnings) {
                    for (const key in dataWarnings) {
                        showToast(t(dataWarnings[key]), 'warning');
                    }  
                }
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }

    const checkUserRole = async () => {
        setCanApproveTransactions(await userHasRole(Transactions_Approve));
        setCanHardDeleteTransactions(await userHasRole(Transactions_HardDelete));

        const hasRole = await userHasRole(AdminWeb);
        if (!hasRole)
            navigate("/error/403");
    }

    useEffect(() => {
        fetchServiceDetails();
        checkUserRole();
    }, [navigate, showToast]);

    useEffect(() => {
        if (service?.id) {
            fetchTransactionRoutes();
        }
    }, [service, page, sortField, asc, pageSize]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const handleFilterSearch = async (filters: IFilterTransactionRouteModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchTransactionRoutes(filters)
    };

    const handleDeleteClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        if (selectedId) {
            try {
                let result = await deleteById("/api/transactionroutes", selectedId) as AxiosResponse;
                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionRouteDeletedSuccessfully'), 'success');
                    await fetchTransactionRoutes(activeFilters);
                } else if (result.status === 403) {
                    navigate("/error/403");
                } else if (result.status === 401) {
                    navigate("/auth/login");
                } else if (result.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }

            setShowModal(false);
            setSelectedId(null);
        }
    };

    const handleConfirmApprove = async () => {
        if (selectedId) {
            setLoading(true);
            let result = await update(`/api/transactionroutes/approve/${selectedId}`, {}) as AxiosResponse;

            if (result.status === 200) {
                setLoading(false);
                showToast(t('TransactionRouteApprovedSuccessfully'), 'success');
                await fetchTransactionRoutes(activeFilters);
            } else if (result.status === 400 && result.data?.errors) {
                setLoading(false);

                for (const key in result.data.errors) {
                    showToast(t(result.data.errors[key]), 'warning');
                }
            } else if (result.status === 403) {
                setLoading(false);
                showToast(t('MissingRoleForTransactionApprove'), 'error');
            } else if (result.status === 401) {
                navigate('/auth/login');
            } else if (result.status === 500) {
                navigate('/error/500');
            } else {
                throw new Error(t('StatusCode', {code: String(result.status)}));
            }
            setShowApproveModal(false);
            setSelectedId(null);
        }
    }

    const handleCancelDelete = () => {
        setShowModal(false);
        setSelectedId(null);
    };
    const handleCancelApprove = () => {
        setShowApproveModal(false);
        setSelectedId(null);
    };

    const handleApprove = async (id: string, routeLabel: string, isDeleted: boolean) => {
        if (isDeleted) {
            showToast(t('ForbidActionForTransactionApprove'), 'warning');
            return;
        }

        if (!canApproveTransactions) {
            showToast(t('MissingRoleForTransactionApprove'), 'error');
            return;
        }

        let transactionData = await fetchTransactionRouteData(id);
        let missingFields: string[] = [];

        if (!transactionData.routeLabel) missingFields.push(t('RouteLabel'));
        if (!transactionData.className) missingFields.push(t('ClassName'));
        if (!transactionData.serviceConsumingType) missingFields.push(t('ServiceConsumingType'));
        if (!transactionData.operationType) missingFields.push(t('OperationType'));
        if (!transactionData.profileTypeList || transactionData.profileTypeList.length === 0) missingFields.push(t('ProfileType'));

        if (missingFields.length > 0) {
            showToast(`${t("MissingRequiredFields")}: ${missingFields.join(", ")}`, 'warning');
            return;
        }

        setSelectedId(id);
        setSelectedName(routeLabel);
        setShowApproveModal(true);
    };

    const fetchTransactionRouteData = async (id: string): Promise<ITransactionRouteDto> => {
        try {
            let result = await getById("/api/transactionroutes/", id);
            let respStatus = result?.status || result?.response.status;
            let data = {} as ITransactionRouteDto;

            if (respStatus === 200) {
                data = result.data as ITransactionRouteDto;
                return data;
            } else {
                throw new Error(t('StatusCode', {code: String(respStatus)}));
            }
        } catch (error) {
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            return {} as ITransactionRouteDto;
        }
    };

    const changeShowDeteled = async (event: any) => {
        event.preventDefault();
        setActiveFilters({
            ...activeFilters,
            showDeleted: !activeFilters.showDeleted
        })
    }

    const handleRecoveryClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowRestoreModal(true);
    };

    const handleHardDeleteClick = (id: string, name: string) => {
        if (!canHardDeleteTransactions) {
            showToast(t('MissingRoleForTransactionHardDelete'), 'warning');
            return;
        }

        setSelectedId(id);
        setSelectedName(name);
        setShowHardDeleteModal(true);
    };

    const handleConfirmRestore = async () => {
        setLoading(true);
        if (selectedId) {
            try {
                let result = await update(`/api/transactionroutes/restoretransaction/${selectedId}`, {}) as AxiosResponse;
                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionRouteRestoredSuccessfully'), 'success');
                    await fetchTransactionRoutes(activeFilters);
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);

                    for (const key in result.data.errors) {
                        showToast(t(result.data.errors[key]), 'warning');
                    }
                } else if (result.status === 403) {
                    navigate("/error/403");
                } else if (result.status === 401) {
                    navigate("/auth/login");
                } else if (result.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }

            setShowRestoreModal(false);
            setSelectedId(null);
        }
    };

    const handleCancelRestore = () => {
        setShowRestoreModal(false);
        setSelectedId(null);
    };

    const handleConfirmHardDelete = async () => {
        setLoading(true);
        if (selectedId) {
            try {
                let result = await deleteById("/api/transactionroutes/harddeletetransaction", selectedId) as AxiosResponse;
                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionRouteHardDeletedSuccessfully'), 'success');
                    await fetchTransactionRoutes(activeFilters);
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);
                    for (const key in result.data.errors) {
                        showToast(t(key), 'warning');
                    }
                } else if (result.status === 403) {
                    navigate("/error/403");
                } else if (result.status === 401) {
                    navigate("/auth/login");
                } else if (result.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }

            setShowHardDeleteModal(false);
            setSelectedId(null);
        }
    };

    const handleCancelHardDelete = () => {
        setShowHardDeleteModal(false);
        setSelectedId(null);
    };


    return (
        <div className="gg-serviceList container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Services'), href: '/services/list'}, // Active page (no href)
                    {title: t('BreadcrumbServiceTransationRoutes', {serviceName: service.name})} // Active page (no href)
                ]}/>

            <h3 className="mb-3">{t('TransactionRouteList')}</h3>
            
            <button className="btn btn-success gg-btn-submit mb-3" onClick={() => navigate(`/services/${serviceid}/transactions/create`)}>
                {t('Create')}
            </button>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm, values}) => (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Id')}
                                        <Field className="form-control" name="id"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('RouteLabel')}
                                        <Field className="form-control" name="routeLabel" value={values.routeLabel || ""} />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ClassName')}
                                        <Field className="form-control" name="className" value={values.className || ""} />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Location')}
                                        <Field className="form-control" name="postLocation" value={values.postLocation || ""} />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-6 col-xl-4 align-self-end d-flex align-items-center">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState)
                                            }}>
                                        {t('Reset')}
                                    </button>
                                    <label className="form-label w-100 ms-2 small">
                                        <Field type="checkbox" className="form-check-input me-2" name="showDeleted"
                                        checked={activeFilters.showDeleted}
                                        onChange={(e: any) => changeShowDeteled(e)} />
                                        {t('ShowDeleted')}
                                    </label>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            {loading && (<Loader />)}

            <div className="table-responsive">
                <table className="table">
                    <thead>
                    <tr>
                        <th style={{width: "20%"}} onClick={() => handleSort("routeLabel")}>{t('RouteLabel')}
                            <SortDirection isActive={sortField === 'routeLabel'} order={order}/></th>
                        <th style={{width: "20%"}} onClick={() => handleSort("className")}>{t('ClassName')}
                            <SortDirection isActive={sortField === 'className'} order={order}/></th>
                        <th style={{width: "15%"}} onClick={() => handleSort("postLocation")}>{t('Location')}
                            <SortDirection isActive={sortField === 'postLocation'} order={order}/></th>
                        <th style={{width: "15%"}}>Approved</th>
                        <th className="text-wrap" style={{width: "30%"}}>{t('Actions')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                        {transactions.map(transactionRoute => (
                            <tr key={transactionRoute.id}>
                                <td>{transactionRoute.routeLabel}</td>
                                <td>{transactionRoute.className}</td>
                                <td>{transactionRoute.postLocation}</td>
                                <td>
                                    {transactionRoute.isApproved ? 
                                        <CheckCircle className={
                                            `text-success ${(!canApproveTransactions) ? 'cursor-disabled' : ''}`} size={16} /> :
                                        <XCircle className={`text-danger ${(!canApproveTransactions || transactionRoute.isDeleted) ? 'cursor-disabled' : 'cursor-pointer'}`} size={16}
                                            onClick={() => handleApprove(transactionRoute.id, transactionRoute.routeLabel, transactionRoute.isDeleted)} />
                                    }
                                </td>
                                <td className="text-wrap">
                                    {transactionRoute.isDeleted ? <>
                                        <button title={t('Restore')} className="btn btn-warning btn-sm me-2"
                                            onClick={() => handleRecoveryClick(transactionRoute.id, transactionRoute.routeLabel)}>
                                            <Archive size={16} />
                                        </button>
                                        {canHardDeleteTransactions && (
                                            <button title={t('HardDelete')} className="btn btn-danger btn-sm"
                                                onClick={() => handleHardDeleteClick(transactionRoute.id, transactionRoute.routeLabel)}>
                                                <Trash3Fill size={16} />
                                            </button>
                                        )}
                                    </> : <>
                                        <Link title={t('Details')}
                                            to={`/services/${serviceid}/transactions/details/${transactionRoute.id}`}
                                            className="btn btn-sm gg-btn me-2">
                                            <FileEarmarkText size={16}/>
                                        </Link>
                                        <Link title={t('Edit')}
                                            to={`/services/${serviceid}/transactions/edit/${transactionRoute.id}`}
                                            className="btn gg-btn btn-sm me-2">
                                            <Pencil size={16} className="text-danger" />
                                        </Link>
                                        <button title={t('Delete')} className="btn btn-danger btn-sm me-2"
                                                onClick={() => handleDeleteClick(transactionRoute.id, transactionRoute.routeLabel)}>
                                            <Trash3 size={16}/>
                                        </button>
                                        <Link title={t('Parameters')}
                                            to={`/services/${serviceid}/transactions/${transactionRoute.id}/transaction-parameters/list`}
                                            className="btn btn-sm gg-btn me-2">
                                            <ListUl size={16}/>
                                        </Link>
                                        <Link title={t('DataDifferences')}
                                            to={`/services/${service.id}/transactions/datadiff/${transactionRoute.id}/list`}
                                            className="btn gg-btn btn-sm me-2">
                                            <FileDiff size={16}/>
                                        </Link>
                                        <Link
                                            title={t('Libraries')}
                                            to={`/services/${serviceid}/transactions/${transactionRoute.id}/libraries/list`}
                                            className="btn btn-sm me-2 btn-dark"
                                        >
                                            <JournalBookmark size={16} />
                                        </Link>
                                        <Link title={t('ClaimLogicBuilder')} to={`/services/${service.id}/transactions/claimbuilder/${transactionRoute.id}`} className="btn btn-sm btn-warning">
                                            <FileLock2 size={16}/>
                                        </Link>
                                    </>}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                show={showModal}
                title={t('Delete')}
                message={t('ItemDeletionConfirmation', {selectedName: selectedName})}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                loading={loading}
            />

            <ConfirmModal
                show={showApproveModal}
                title={t('Approve')}
                message={t('ItemApproveConfirmation', {selectedName: selectedName})}
                onConfirm={handleConfirmApprove}
                onCancel={handleCancelApprove}
                buttonClass={'warning'}
                loading={loading}
            />

            <ConfirmModal
                show={showRestoreModal}
                title={t('Restore')}
                message={t('ItemRestoreConfirmation', {selectedName: selectedName})}
                onConfirm={handleConfirmRestore}
                onCancel={handleCancelRestore}
                buttonClass={'warning'}
                loading={loading}
            />

            <ConfirmModal
                show={showHardDeleteModal}
                title={t('HardDelete')}
                message={t('ItemHardDeletionConfirmation', {selectedName: selectedName})}
                onConfirm={handleConfirmHardDelete}
                onCancel={handleCancelHardDelete}
                loading={loading}
            />

            <Pagination currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                totalItems={total}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size) }} />

        </div>
    );
};

export default TransationList;
